import {attr, controller, target} from '@github/catalyst'

@controller('annotation-message')
class AnnotationMessageElement extends HTMLElement {
  @target declare showMoreButton: HTMLButtonElement
  @target declare showLessButton: HTMLButtonElement
  @target declare annotationContainer: HTMLElement
  @attr expanded = false

  declare private annotationTextComponent: HTMLElement

  connectedCallback() {
    this.annotationTextComponent = this.annotationContainer.firstElementChild as HTMLElement
    this.initalizeEventListeners()
  }

  static observedAttributes = ['data-expanded']
  attributeChangedCallback() {
    if (this.expanded) {
      this.displayShowLessButton()
      this.annotationContainer.classList.replace('annotation--contracted', 'annotation--expanded')
    } else {
      this.displayShowMoreButton()
      this.annotationContainer.classList.replace('annotation--expanded', 'annotation--contracted')
    }
  }

  toggleExpansion() {
    this.expanded = !this.expanded
  }

  private calculateOverflow() {
    if (this.annotationTextComponent) {
      const isEllipsisPresent = this.isEllipsisActive(this.annotationTextComponent)
      if (!this.expanded) {
        if (isEllipsisPresent) {
          this.displayShowMoreButton()
        } else {
          this.hideBothButtons()
        }
      } else {
        if (!isEllipsisPresent) {
          const wrapped = this.isAnnotationWrapped(this.annotationTextComponent)
          if (this.expanded && wrapped) {
            this.displayShowLessButton()
          } else if (wrapped) {
            this.displayShowMoreButton()
          } else {
            this.hideBothButtons()
          }
        }
      }
    }
  }

  private displayShowLessButton() {
    this.setButtonHiddenProperties(false, true)
  }

  private displayShowMoreButton() {
    this.setButtonHiddenProperties(true, false)
  }

  private hideBothButtons() {
    this.setButtonHiddenProperties(true, true)
  }

  private setButtonHiddenProperties(isShowLessHidden: boolean, isShowMoreHidden: boolean) {
    this.showLessButton.hidden = isShowLessHidden
    this.showMoreButton.hidden = isShowMoreHidden
  }

  /**
   * When the page gets resized, text overflow can change and the visibility of the toggleButton may need to
   * be adjusted. There is no native cross-browser event that is fired when an element overflows so we listen
   * to the resize event and use an elements computed line height to detect if overflow has happened
   */
  private initalizeEventListeners() {
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => this.calculateOverflow()).observe(this.annotationTextComponent)
    } else {
      /* eslint-disable-next-line github/prefer-observers */ // @ts-expect-error Certain old browsers like Safari < 13.1 & IE do not have ResizeObserver
      window.addEventListener('resize', () => this.calculateOverflow())
    }
  }

  // from: https://stackoverflow.com/questions/7738117/html-text-overflow-ellipsis-detection
  private isEllipsisActive(e: HTMLElement) {
    return e.offsetWidth < e.scrollWidth
  }

  // from: https://stackoverflow.com/questions/53280724/word-wrap-detection-in-javascript
  private isAnnotationWrapped(e: HTMLElement) {
    const {lineHeight} = getComputedStyle(e)
    const parsedLineHeight = parseInt(lineHeight.split('px')[0]!)
    return e.scrollHeight > parsedLineHeight
  }
}
